import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { useSocket } from '../../context/SocketContext';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { 
  FiShoppingCart, 
  FiPlus, 
  FiMinus, 
  FiPrinter, 
  FiSearch,
  FiTrash2,
  FiClock,
  FiCheck,
  FiMaximize2,
  FiMinimize2,
  FiImage,
  FiEyeOff,
  FiX,
  FiEdit2
} from 'react-icons/fi';

const DEFAULT_PAYMENT_METHODS = ['cash', 'card', 'upi', 'wallet'];

function parsePaymentMethods(value) {
  const methods = String(value || '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

  const uniqueMethods = [...new Set(methods)];
  return uniqueMethods.length > 0 ? uniqueMethods : DEFAULT_PAYMENT_METHODS;
}

function paymentMethodLabel(value) {
  const method = String(value || '').trim().toLowerCase();
  if (!method) return 'Other';
  if (method === 'upi') return 'UPI';
  return method.charAt(0).toUpperCase() + method.slice(1);
}

function POS() {
  const socket = useSocket();
  const { alert, confirm } = useModal();
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [placedOrders, setPlacedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showImages, setShowImages] = useState(true); // Toggle for images
  const [paymentMethod, setPaymentMethod] = useState('cash'); // Payment method: cash, card, upi, wallet
  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [billPrintCopies, setBillPrintCopies] = useState(1);
  const [includeGstInBill, setIncludeGstInBill] = useState(true);
  const [includeServiceCharge, setIncludeServiceCharge] = useState(false); // Service charge toggle (default OFF)
  const [applyDiscount, setApplyDiscount] = useState(false); // Discount toggle
  const [discountPercent, setDiscountPercent] = useState(''); // Discount % for bill generation
  const [discountType, setDiscountType] = useState('percent'); // 'percent' or 'amount'
  const [discountFlatAmount, setDiscountFlatAmount] = useState(''); // Flat ₹ discount
  const [cashierName, setCashierName] = useState(''); // Cashier name
  const [generatingBill, setGeneratingBill] = useState(false); // Track bill generation state
  const [placingOrder, setPlacingOrder] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false); // Show bill generation modal
  const [selectedOrderForBill, setSelectedOrderForBill] = useState(null); // Order selected for bill generation
  const [showPreBillEditModal, setShowPreBillEditModal] = useState(false);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);
  const [editOrderItems, setEditOrderItems] = useState([]);
  const [savingPreBillEdit, setSavingPreBillEdit] = useState(false);
  const [showTableTransferModal, setShowTableTransferModal] = useState(false);
  const [transferTargetTableId, setTransferTargetTableId] = useState('');
  const [transferringTable, setTransferringTable] = useState(false);
  const cashierInputRef = useRef(null);
  const discountInputRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false); // Fullscreen state
  const posRef = useRef(null);

  // Fullscreen handlers
  const enterFullscreen = () => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const exitFullscreen = () => {
    setIsFullscreen(false);
    document.body.style.overflow = '';
  };

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Escape' || e.key === 'Esc') && isFullscreen) {
        exitFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  // Cleanup body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (showBillModal) {
      setTimeout(() => {
        // Focus cashier only if user is not already typing in another input
        if (applyDiscount) return;
        const activeTag = document.activeElement?.tagName;
        const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeTag);
        if (!isTyping) {
          cashierInputRef.current?.focus();
        }
      }, 50);
    }
  }, [showBillModal, applyDiscount]);

  useEffect(() => {
    if (showBillModal && applyDiscount) {
      setTimeout(() => {
        discountInputRef.current?.focus();
      }, 60);
    }
  }, [showBillModal, applyDiscount]);

  const handleDiscountPercentChange = (value) => {
    // Allow only digits and one decimal point to avoid odd mobile keyboard behavior
    let cleaned = String(value || '').replace(/[^0-9.]/g, '');
    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    }
    setDiscountPercent(cleaned);
  };

  useEffect(() => {
    fetchData();
    
    if (socket) {
      socket.on('order-updated', () => {
        if (selectedTable) {
          fetchPlacedOrders();
        }
        fetchTables();
      });
    }

    return () => {
      if (socket) {
        socket.off('order-updated');
      }
    };
  }, [socket, selectedTable]);

  useEffect(() => {
    if (selectedTable) {
      fetchPlacedOrders();
      setCart([]); // Clear cart when table changes
    } else {
      setPlacedOrders([]);
      setCart([]);
    }
  }, [selectedTable]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchTables(),
      fetchCategories(),
      fetchMenuItems(),
      fetchPaymentMethods()
    ]);
    setLoading(false);
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get('/api/settings/restaurant');
      const configured = parsePaymentMethods(response.data?.payment_methods);
      const configuredDefault = String(response.data?.default_payment_method || '').trim().toLowerCase();
      const resolvedDefault = configured.includes(configuredDefault)
        ? configuredDefault
        : (configured[0] || 'cash');
      const parsedCopies = parseInt(String(response.data?.bill_print_copies || '1'), 10);
      const resolvedCopies = Number.isFinite(parsedCopies) && parsedCopies > 0 ? Math.min(5, parsedCopies) : 1;
      const gstEnabled = String(response.data?.include_gst_in_bill ?? 'true').trim().toLowerCase() !== 'false';
      setIncludeGstInBill(gstEnabled);
      setPaymentMethods(configured);
      setPaymentMethod(resolvedDefault);
      setBillPrintCopies(resolvedCopies);
    } catch (error) {
      console.error('Failed to fetch POS payment methods, using defaults:', error);
      setIncludeGstInBill(true);
      setPaymentMethods(DEFAULT_PAYMENT_METHODS);
      setPaymentMethod(DEFAULT_PAYMENT_METHODS[0]);
      setBillPrintCopies(1);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await api.get('/api/tables');
      setTables(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/menu/categories');
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await api.get('/api/menu?availableOnly=true');
      setMenuItems(response.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    }
  };

  const fetchPlacedOrders = async () => {
    if (!selectedTable) return;

    try {
      const response = await api.get(`/api/pos/tables/${selectedTable}/orders`);
      const orders = response.data?.orders || [];
      
      // Get all active orders for this table (excluding paid/completed which are in bills section)
      // Show orders that haven't been paid yet (status can be anything except paid)
      const activeOrders = orders.filter(order => 
        order.payment_status !== 'paid' && order.status !== 'cancelled'
      );

      // Flatten order items with order info - use item-level status
      const allOrderItems = [];
      activeOrders.forEach(order => {
        if (order.order_items && order.order_items.length > 0) {
          order.order_items.forEach(item => {
            allOrderItems.push({
              ...item,
              orderId: order.id,
              orderNumber: order.order_number,
              orderStatus: order.status, // Keep for reference
              itemStatus: item.status || 'pending', // Use item-level status
              orderCreatedAt: order.created_at,
              orderSubtotal: order.subtotal,
              orderTax: order.tax,
              orderTotal: order.total,
              isCompleted: order.status === 'completed'
            });
          });
        }
      });

      setPlacedOrders(allOrderItems);
    } catch (error) {
      console.error('Failed to fetch placed orders:', error);
      setPlacedOrders([]);
    }
  };

  const addToCart = async (item) => {
    if (!selectedTable) {
      await alert('Please select a table first', 'Table Required', 'warning');
      return;
    }

    // Default behaviour: merge same dish without special instructions (Spring Roll x2)
    // Only create a separate line when we later add a special instruction.
    setCart((prev) => {
      const idx = prev.findIndex(
        (line) =>
          line.id === item.id &&
          (!line.specialInstructions || line.specialInstructions.trim() === '')
      );

      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          quantity: updated[idx].quantity + 1,
        };
        return updated;
      }

      return [
        ...prev,
        {
          ...item,
          quantity: 1,
          apcUnitPrice: item.is_apc ? Number(item.price) || 0 : null,
          specialInstructions: '',
        },
      ];
    });
  };

  const getCartItemUnitPrice = (item) => {
    if (!item?.is_apc) {
      return Number(item?.price) || 0;
    }

    if (item.apcUnitPrice === '' || item.apcUnitPrice === null || item.apcUnitPrice === undefined) {
      return Number(item?.price) || 0;
    }

    const apcPrice = Number(item.apcUnitPrice);
    if (!Number.isFinite(apcPrice) || apcPrice < 0) {
      return Number(item?.price) || 0;
    }

    return apcPrice;
  };

  const updateApcUnitPrice = (index, value) => {
    setCart((prev) => prev.map((item, i) => {
      if (i !== index) return item;
      return {
        ...item,
        apcUnitPrice: value === '' ? '' : value
      };
    }));
  };

  const updateCartQuantity = (index, change) => {
    setCart((prev) => {
      const updated = [...prev];
      const item = updated[index];
      if (!item) return prev;

      const newQuantity = item.quantity + change;
      if (newQuantity <= 0) {
        updated.splice(index, 1);
      } else {
        updated[index] = { ...item, quantity: newQuantity };
      }
      return updated;
    });
  };

  const removeFromCart = (index) => {
    setCart((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const updateSpecialInstructions = (index, instructions) => {
    setCart((prev) => {
      const updated = [...prev];
      const item = updated[index];
      if (!item) return prev;

      updated[index] = { ...item, specialInstructions: instructions };
      return updated;
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  const calculateCartTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + (getCartItemUnitPrice(item) * item.quantity), 0);
    const cgst = includeGstInBill ? subtotal * 0.025 : 0; // 2.5% CGST
    const sgst = includeGstInBill ? subtotal * 0.025 : 0; // 2.5% SGST
    const tax = cgst + sgst; // Total tax = 5%
    return { subtotal, cgst, sgst, tax, total: subtotal + tax };
  };

  const confirmAndPlaceOrder = async () => {
    if (placingOrder) {
      return;
    }

    if (!selectedTable) {
      await alert('Please select a table first', 'Table Required', 'warning');
      return;
    }

    if (cart.length === 0) {
      await alert('Cart is empty', 'Empty Cart', 'warning');
      return;
    }

    setPlacingOrder(true);
    try {
      const orderItems = cart.map(item => ({
        menuItemId: item.id,
        quantity: item.quantity,
        specialInstructions: item.specialInstructions || '',
        ...(item.is_apc ? { unitPrice: getCartItemUnitPrice(item) } : {})
      }));

      const response = await api.post('/api/orders', {
        tableId: selectedTable,
        guestType: 'restaurant',
        orderType: 'dine_in',
        orderSource: 'staff',
        items: orderItems
      });

      console.log('Order placed:', response.data);
      
      // Emit socket event for real-time updates
      if (socket) {
        socket.emit('order-updated', { 
          orderId: response.data.order?.id,
          tableId: selectedTable,
          status: 'pending'
        });
      }

      await alert('Order placed successfully! Items sent to kitchen.', 'Success', 'success');
      clearCart();
      
      // Refresh orders and tables immediately
      await fetchPlacedOrders();
      await fetchTables();
      
      // Also refresh after a short delay to ensure data is synced
      setTimeout(() => {
        fetchPlacedOrders();
      }, 500);
    } catch (error) {
      console.error('Failed to place order:', error);
      const errorMessage = error.response?.data?.error || 'Failed to place order';
      await alert(errorMessage, 'Error', 'error');
    } finally {
      setPlacingOrder(false);
    }
  };

  const handleGenerateBillClick = (orderId, orderNumber, subtotal) => {
    setSelectedOrderForBill({ orderId, orderNumber, subtotal: parseFloat(subtotal || 0) });
    setIncludeServiceCharge(false);
    setApplyDiscount(false);
    setDiscountPercent('');
    setDiscountType('percent');
    setDiscountFlatAmount('');
    setShowBillModal(true);
  };

  const openPreBillEditModal = (orderGroup) => {
    const draftItems = (orderGroup.items || []).map((item, index) => ({
      lineId: `${item.id || item.menu_item_id || 'line'}-${index}`,
      menuItemId: item.menu_item_id,
      quantity: parseInt(item.quantity, 10) || 1,
      specialInstructions: item.special_instructions || ''
    }));

    setSelectedOrderForEdit({
      orderId: orderGroup.orderId,
      orderNumber: orderGroup.orderNumber
    });
    setEditOrderItems(draftItems.length > 0 ? draftItems : []);
    setShowPreBillEditModal(true);
  };

  const addPreBillEditLine = () => {
    if (!menuItems || menuItems.length === 0) return;

    const defaultMenu = menuItems[0];
    setEditOrderItems((prev) => ([
      ...prev,
      {
        lineId: `new-${Date.now()}-${prev.length}`,
        menuItemId: defaultMenu.id,
        quantity: 1,
        specialInstructions: ''
      }
    ]));
  };

  const updatePreBillEditLine = (lineId, updates) => {
    setEditOrderItems((prev) => prev.map((line) => {
      if (line.lineId !== lineId) return line;
      return {
        ...line,
        ...updates
      };
    }));
  };

  const removePreBillEditLine = (lineId) => {
    setEditOrderItems((prev) => prev.filter((line) => line.lineId !== lineId));
  };

  const closePreBillEditModal = (force = false) => {
    if (savingPreBillEdit && !force) return;
    setShowPreBillEditModal(false);
    setSelectedOrderForEdit(null);
    setEditOrderItems([]);
  };

  const openTableTransferModal = async () => {
    if (!selectedTable) {
      await alert('Please select a table first', 'Table Required', 'warning');
      return;
    }

    await fetchTables();
    setTransferTargetTableId('');
    setShowTableTransferModal(true);
  };

  const closeTableTransferModal = (force = false) => {
    if (transferringTable && !force) return;
    setShowTableTransferModal(false);
    setTransferTargetTableId('');
  };

  const handleTableTransfer = async () => {
    if (!selectedTable) {
      await alert('Please select a table first', 'Table Required', 'warning');
      return;
    }

    if (!transferTargetTableId) {
      await alert('Please choose a target table', 'Target Table Required', 'warning');
      return;
    }

    const sourceTableId = selectedTable;
    const targetTableId = transferTargetTableId;
    const sourceTableNumber = tables.find((table) => table.id === sourceTableId)?.table_number || '';
    const targetTableNumber = tables.find((table) => table.id === targetTableId)?.table_number || '';

    // Close primary modal first so confirm modal never appears behind it.
    closeTableTransferModal(true);

    const confirmed = await confirm(
      `Transfer all active orders from ${sourceTableNumber} to ${targetTableNumber}?`,
      'Confirm Table Transfer',
      'warning'
    );

    if (!confirmed) {
      setTransferTargetTableId(targetTableId);
      setShowTableTransferModal(true);
      return;
    }

    try {
      setTransferringTable(true);

      const response = await api.post(`/api/pos/tables/${sourceTableId}/transfer`, {
        targetTableId
      });

      const nextTableId = targetTableId;
      closeTableTransferModal(true);
      setSelectedTable(nextTableId);

      await fetchTables();

      if (socket) {
        socket.emit('order-updated', {
          status: 'table_transferred',
          sourceTableId,
          targetTableId: nextTableId
        });
      }

      await alert(response.data?.message || 'Table transferred successfully', 'Success', 'success');
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to transfer table';
      await alert(message, 'Transfer Failed', 'error');
    } finally {
      setTransferringTable(false);
    }
  };

  const savePreBillEdit = async () => {
    if (!selectedOrderForEdit?.orderId || savingPreBillEdit) {
      return;
    }

    const editTarget = {
      orderId: selectedOrderForEdit.orderId,
      orderNumber: selectedOrderForEdit.orderNumber
    };

    const payloadItems = editOrderItems
      .map((line) => ({
        menuItemId: line.menuItemId,
        quantity: Math.max(0, parseInt(line.quantity, 10) || 0),
        specialInstructions: String(line.specialInstructions || '').trim()
      }))
      .filter((line) => line.menuItemId && line.quantity > 0);

    if (payloadItems.length === 0) {
      const draftSnapshot = editOrderItems.map((line) => ({ ...line }));
      closePreBillEditModal(true);

      const shouldCancelOrder = await confirm(
        `This will remove all items and cancel Order #${editTarget.orderNumber}. Continue?`,
        'Cancel Order'
      );

      if (!shouldCancelOrder) {
        setSelectedOrderForEdit(editTarget);
        setEditOrderItems(draftSnapshot);
        setShowPreBillEditModal(true);
        return;
      }
    }

    setSavingPreBillEdit(true);
    try {
      const response = await api.post(
        `/api/pos/orders/${editTarget.orderId}/pre-bill-edit`,
        { items: payloadItems }
      );

      // Close the editor modal before opening any secondary modal/alert to avoid stacked modal deadlocks.
      closePreBillEditModal(true);
      await fetchPlacedOrders();
      await fetchTables();

      if (socket) {
        socket.emit('order-updated', {
          orderId: editTarget.orderId,
          status: response.data?.cancelled ? 'cancelled' : 'updated'
        });
      }

      await alert(
        response.data?.message || 'Order items updated successfully.',
        response.data?.cancelled ? 'Order Cancelled' : 'Order Updated',
        'success'
      );
    } catch (error) {
      console.error('Failed to save pre-bill edits:', error);
      const message = error.response?.data?.error || 'Failed to update order items';
      await alert(message, 'Update Failed', 'error');
    } finally {
      setSavingPreBillEdit(false);
    }
  };

  const generateBill = async (orderId, orderNumber, discountAmount = 0) => {
    // Close modal
    setShowBillModal(false);
    setSelectedOrderForBill(null);
    
    if (generatingBill) {
      await alert('Bill generation is already in progress. Please wait...', 'Please Wait', 'warning');
      return;
    }

    setGeneratingBill(true);

    try {
      // Debug: Log the values being sent
      const serviceChargeValue = Boolean(includeServiceCharge);
      console.log('=== FRONTEND: GENERATING BILL ===');
      console.log('Order ID:', orderId);
      console.log('Payment Method:', paymentMethod);
      console.log('Include Service Charge:', serviceChargeValue, '(type:', typeof serviceChargeValue + ')');
      console.log('Raw checkbox value:', includeServiceCharge);
      console.log('===================================');
      
      // Generate bill (this will create bill, mark order as completed, and free table)
      const response = await api.post(
        `/api/bills/generate/${orderId}`, 
        { 
          paymentMethod: paymentMethod,
          includeServiceCharge: serviceChargeValue,
          discountAmount: parseFloat(discountAmount || 0),
          cashierName: cashierName.trim() || null
        },
        { 
          responseType: 'blob',
          validateStatus: () => true // Accept all status codes to handle manually
        }
      );

      // Get billId by querying bills with orderId (with retry logic)
      let billId = null;
      const getBillId = async (retries = 3) => {
        for (let i = 0; i < retries; i++) {
          try {
            await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before querying
            const billsResponse = await api.get(`/api/bills?orderId=${orderId}`);
            if (billsResponse.data?.bills && billsResponse.data.bills.length > 0) {
              return billsResponse.data.bills[0].id;
            }
          } catch (billQueryError) {
            console.error(`Error fetching bill ID (attempt ${i + 1}):`, billQueryError);
            if (i === retries - 1) {
              // Last attempt failed
              return null;
            }
          }
        }
        return null;
      };
      
      billId = await getBillId();

      if (billId) {
        await printThermalBill(billId, billPrintCopies);
      } else {
        await alert('Bill generated but could not fetch bill ID for printing.', 'Print Warning', 'warning');
      }

      // Bill created successfully - show success message
      const responseData = response.data;
      await alert(`Bill created successfully! ${responseData.message || 'Order moved to Bills section and table is now available.'}\n\nYou can print the bill from the Bills page using the print-agent.`, 'Bill Created', 'success');

      // Refresh data after bill is generated
      setTimeout(async () => {
        await fetchPlacedOrders();
        await fetchTables();
        if (socket) {
          socket.emit('order-updated', { orderId, status: 'completed' });
          socket.emit('kds-updated');
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to generate bill:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to generate bill';
      await alert(`Error: ${errorMessage}. The bill may have been created. Please check the Bills section.`, 'Error', 'error');
      
      // Refresh data even on error
      setTimeout(async () => {
        await fetchPlacedOrders();
        await fetchTables();
        if (socket) {
          socket.emit('order-updated', { orderId, status: 'completed' });
          socket.emit('kds-updated');
        }
      }, 1000);
    } finally {
      setGeneratingBill(false);
      setApplyDiscount(false);
      setDiscountPercent('');
      setDiscountFlatAmount('');
      setDiscountType('percent');
    }
  };

  const printThermalBill = async (billId, copies = 1) => {
    try {
      const printJobResponse = await api.get(`/api/bills/${billId}/print-job`);
      const printJob = printJobResponse.data;
      const copyCount = Number.isFinite(Number(copies)) && Number(copies) > 0 ? Math.min(5, Number(copies)) : 1;

      const getLocalPrintServiceUrls = () => {
        const urls = [];
        const seen = new Set();
        const add = (value) => {
          if (!value) return;
          const normalized = String(value).replace(/\/$/, '');
          if (seen.has(normalized)) return;
          seen.add(normalized);
          urls.push(normalized);
        };

        const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
          ? String(window.location.origin).replace(/\/$/, '')
          : '';
        const port = (typeof window !== 'undefined' && window.location)
          ? String(window.location.port || '')
          : '';

        if (port === '9101') {
          add(origin);
        }

        add(process.env.REACT_APP_PRINT_AGENT_URL);
        add('http://localhost:9101');
        add('http://127.0.0.1:9101');
        return urls;
      };

      const postToLocalPrintService = async (job) => {
        const urls = getLocalPrintServiceUrls();
        let lastError = null;

        for (const baseUrl of urls) {
          try {
            const response = await fetch(`${baseUrl}/print`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(job)
            });

            if (!response.ok) {
              let message = `HTTP ${response.status}`;
              try {
                const data = await response.clone().json();
                message = data.error || data.message || message;
              } catch (e) {
                // ignore parse error
              }
              lastError = new Error(message);
              continue;
            }

            return response;
          } catch (error) {
            lastError = error;
          }
        }

        throw lastError || new Error('No local print service is reachable');
      };

      for (let copyIndex = 0; copyIndex < copyCount; copyIndex++) {
        const response = await postToLocalPrintService(printJob);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
          throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/octet-stream')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `bill-${billId}.escpos`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          await alert('⚠️ No printer found!\n\nESC/POS file downloaded instead.\n\nTo print:\n1. Connect a USB thermal printer\n2. Or configure a LAN printer\n3. Then try printing again', 'Printer Not Found', 'warning');
          return;
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || result.error || 'Print failed');
        }
      }

      await alert(`✅ Bill sent to thermal printer successfully (${copyCount} ${copyCount > 1 ? 'copies' : 'copy'})!`, 'Success', 'success');
    } catch (error) {
      console.error('Failed to print thermal bill:', error);

      if (error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.includes('Network request failed') ||
          error.message.includes('ERR_CONNECTION_REFUSED') ||
          error.name === 'TypeError') {
        await alert('❌ Local print service is not running!\n\nPlease:\n1. Start print-agent: cd print-agent && npm start\n2. Ensure it is running on http://localhost:9101\n3. Try again', 'Print Agent Not Running', 'error');
      } else {
        await alert(`❌ Failed to print: ${error.message || 'Unknown error'}\n\nMake sure:\n- Print-agent is running on localhost:9101\n- Printer is connected and configured`, 'Print Failed', 'error');
      }
    }
  };

  // Convert to IST (Indian Standard Time) - Proper timezone handling
  const formatIST = (dateString) => {
    if (!dateString) return '';
    try {
      // Ensure timestamp is treated as UTC
      let date;
      if (typeof dateString === 'string') {
        // If timestamp doesn't have timezone info, append 'Z' to force UTC interpretation
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.match(/-\d{2}:\d{2}$/)) {
          // No timezone indicator - append 'Z' to force UTC interpretation
          date = new Date(dateString + 'Z');
        } else {
          // Has timezone info, parse normally
          date = new Date(dateString);
        }
      } else {
        date = dateString;
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return '';
      }
      
      // Format directly in IST timezone
      // The date object stores time in UTC internally, so toLocaleString with timeZone will convert correctly
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  const getMinutesSinceOrder = (timestamp) => {
    if (!timestamp) return 0;
    try {
      // Parse timestamp - ensure it's treated correctly
      let orderTime;
      
      if (typeof timestamp === 'string') {
        // If timestamp is a string, check if it has timezone info
        // Supabase returns timestamps in ISO format, usually with 'Z' or timezone offset
        // If no timezone, assume UTC
        if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
          // No timezone indicator - append 'Z' to force UTC interpretation
          orderTime = new Date(timestamp + 'Z');
        } else {
          // Has timezone info, parse normally
          orderTime = new Date(timestamp);
        }
      } else {
        // Already a Date object
        orderTime = timestamp;
      }
      
      if (isNaN(orderTime.getTime())) {
        console.error('Invalid timestamp:', timestamp);
        return 0;
      }
      
      // Get current time
      const now = new Date();
      
      // Calculate difference in milliseconds
      // Both are in UTC internally (getTime() returns UTC milliseconds)
      const diffMs = now.getTime() - orderTime.getTime();
      
      // Convert to minutes
      const diffMinutes = Math.floor(diffMs / 60000);
      
      // If difference is negative or suspiciously large (like 330 mins = 5.5 hours = IST offset),
      // it's likely a timezone parsing issue
      if (diffMinutes < 0 || diffMinutes > 1440) {
        // Try alternative: parse as if it's already in IST
        // This handles cases where DB returns IST-formatted strings without timezone
        const orderTimeIST = new Date(timestamp);
        const nowIST = new Date();
        const diffMsIST = nowIST.getTime() - orderTimeIST.getTime();
        const diffMinutesIST = Math.floor(diffMsIST / 60000);
        
        // If IST calculation gives reasonable result, use it
        if (diffMinutesIST >= 0 && diffMinutesIST <= 1440) {
          return diffMinutesIST;
        }
        
        // If still wrong, return 0 and log
        console.warn('Time calculation issue:', {
          original: diffMinutes,
          ist: diffMinutesIST,
          timestamp,
          orderTimeUTC: orderTime.toISOString(),
          nowUTC: now.toISOString()
        });
        return 0;
      }
      
      return diffMinutes;
    } catch (error) {
      console.error('Error calculating minutes:', error, 'timestamp:', timestamp);
      return 0;
    }
  };

  const normalizeSearchValue = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const normalizedSearchTerm = normalizeSearchValue(searchTerm);

  const filteredItems = menuItems.filter((item) => {
    const nameNormalized = normalizeSearchValue(item.name);
    const nameWords = nameNormalized.length > 0 ? nameNormalized.split(' ') : [];

    const startsWithName = normalizedSearchTerm && nameNormalized.startsWith(normalizedSearchTerm);
    const startsWithAnyWord = normalizedSearchTerm && nameWords.some((word) => word.startsWith(normalizedSearchTerm));
    const matchesSearch = !normalizedSearchTerm || startsWithName || startsWithAnyWord;

    const matchesCategory = normalizedSearchTerm
      ? true
      : (!selectedCategory || item.category_id === selectedCategory);

    return matchesCategory && matchesSearch && item.is_available;
  });

  const cartTotal = calculateCartTotal();

  const sortedTables = [...tables].sort((a, b) => {
    const aLabel = String(a?.table_number ?? '').trim();
    const bLabel = String(b?.table_number ?? '').trim();
    const natural = aLabel.localeCompare(bLabel, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
    if (natural !== 0) return natural;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''), undefined, { numeric: true });
  });

  // Group placed orders by order number
  const groupedPlacedOrders = placedOrders.reduce((acc, item) => {
    const orderKey = item.orderNumber;
    if (!acc[orderKey]) {
      acc[orderKey] = {
        orderId: item.orderId,
        orderNumber: item.orderNumber,
        orderStatus: item.orderStatus,
        orderCreatedAt: item.orderCreatedAt,
        orderSubtotal: item.orderSubtotal || 0,
        orderTax: item.orderTax || 0,
        orderTotal: item.orderTotal || 0,
        items: []
      };
    }
    acc[orderKey].items.push(item);
    return acc;
  }, {});

  const availableTransferTables = sortedTables.filter((table) => (
    table.id !== selectedTable && String(table.status || '').toLowerCase() === 'available'
  ));

  const clubOrderItems = (items = []) => {
    const grouped = {};

    items.forEach((item) => {
      const name = item.menu_items?.name || 'Item';
      const key = [
        item.menu_item_id || name,
        Number(item.unit_price || 0).toFixed(2),
        String(item.special_instructions || '').trim().toLowerCase()
      ].join('|');

      if (!grouped[key]) {
        grouped[key] = {
          key,
          name,
          unitPrice: Number(item.unit_price || 0),
          totalQty: 0,
          totalSubtotal: 0,
          specialInstructions: item.special_instructions || '',
          entries: []
        };
      }

      const qty = Number(item.quantity || 0);
      const subtotal = Number(item.subtotal || (qty * Number(item.unit_price || 0)) || 0);
      const status = item.itemStatus || item.status || 'pending';
      const itemTime = item.created_at || item.orderCreatedAt;

      grouped[key].totalQty += qty;
      grouped[key].totalSubtotal += subtotal;
      grouped[key].entries.push({
        qty,
        status,
        itemTime
      });
    });

    return Object.values(grouped);
  };

  // POS Content - defined before loading check
  const posContent = (
    <div 
      ref={posRef}
      className={`${isFullscreen ? 'fixed inset-0 w-screen h-screen overflow-auto bg-gray-50 z-50' : 'bg-gray-50'} ${isFullscreen ? 'p-4' : ''}`}
    >
      <div className={`${isFullscreen ? 'max-w-[1920px] mx-auto' : ''}`}>
        {/* Fullscreen Header */}
        {isFullscreen && (
          <div className="flex justify-between items-center mb-4 bg-white rounded-lg border-0 shadow-sm p-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Point of Sale</h1>
              <p className="text-sm text-gray-600">Table Management & Order Processing</p>
            </div>
            <button
              onClick={exitFullscreen}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <FiMinimize2 className="h-4 w-4" /> Exit Fullscreen
            </button>
          </div>
        )}

        <div className={`flex flex-col lg:flex-row gap-4 ${isFullscreen ? 'h-[calc(100vh-140px)]' : 'h-[calc(100vh-120px)]'}`}>
        {/* Left Panel - Menu Items - Redesigned for better accessibility */}
        <div className="flex-1 flex flex-col bg-white rounded-lg border-0 shadow-sm overflow-hidden min-w-0">
          {/* Sticky Header - Compact */}
          <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-white">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-bold text-gray-900">Menu Items</h2>
              {!isFullscreen && (
                <button
                  onClick={enterFullscreen}
                  className="bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 flex items-center gap-2 text-xs font-medium transition-colors"
                  title="Enter fullscreen mode"
                >
                  <FiMaximize2 className="h-3 w-3" /> Fullscreen
                </button>
              )}
            </div>

            {/* Table Selection - Compact */}
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Table *
              </label>
              <div className="flex gap-1.5 flex-wrap max-h-20 overflow-y-auto scrollbar-hide">
                {sortedTables.map((table) => (
                  <button
                    key={table.id}
                    onClick={() => setSelectedTable(table.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      selectedTable === table.id
                        ? 'bg-gray-900 text-white'
                        : table.status === 'occupied'
                        ? 'bg-red-100 text-red-800 border border-red-300'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {table.table_number}
                    {table.status === 'occupied' && ' ⚠'}
                  </button>
                ))}
              </div>
            </div>

            {selectedTable && (
              <>
                {/* Search - Compact */}
                <div className="relative mb-3">
                  <FiSearch className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search items across menu..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>

                {/* Categories and Image Toggle Row */}
                <div className="flex items-center justify-between gap-3 mb-3">
                  {/* Categories - Compact horizontal scroll */}
                  <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide flex-1">
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                        !selectedCategory
                          ? 'bg-gray-900 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      All
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                          selectedCategory === category.id
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>

                  {/* Image Toggle - Improved Design */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-gray-600 font-medium">Images</span>
                    <button
                      onClick={() => setShowImages(!showImages)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 ${
                        showImages ? 'bg-gray-900' : 'bg-gray-300'
                      }`}
                      title={showImages ? 'Hide images' : 'Show images'}
                      role="switch"
                      aria-checked={showImages}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          showImages ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Menu Items Grid - Maximized space with more columns */}
          {!selectedTable ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center text-gray-500">
                <p className="text-sm mb-1">Please select a table to start ordering</p>
                <p className="text-xs">Choose a table from above to view menu items</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto min-h-0">
              {filteredItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-sm">No menu items found</p>
                </div>
              ) : (
                <div className={`grid ${
                  showImages 
                    ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 p-4' 
                    : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 p-4'
                }`}>
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className={`bg-white border border-gray-200 rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer flex flex-col group overflow-hidden ${
                        showImages ? 'p-2.5' : 'p-3'
                      }`}
                      onClick={() => addToCart(item)}
                    >
                      {showImages && (
                        item.image_url ? (
                          <div className="w-full aspect-square mb-2 rounded-lg overflow-hidden bg-gray-50">
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="w-full aspect-square mb-2 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                            <FiImage className="text-gray-400 text-lg opacity-50" />
                          </div>
                        )
                      )}
                      <h3 className={`font-semibold text-gray-900 mb-2 line-clamp-2 flex-1 ${
                        showImages 
                          ? 'text-xs leading-tight min-h-[2.5rem]' 
                          : 'text-sm leading-tight min-h-[2.75rem]'
                      }`}>
                        {item.name}
                      </h3>
                      <div className={`flex justify-between items-center mt-auto ${
                        showImages ? 'pt-2 border-t border-gray-100' : 'pt-2 border-t border-gray-100'
                      }`}>
                        <span className={`font-bold text-gray-900 ${
                          showImages ? 'text-sm' : 'text-base'
                        }`}>
                          ₹{item.price}
                        </span>
                        <button 
                          className={`bg-gray-900 text-white hover:bg-gray-800 active:scale-95 transition-all flex-shrink-0 rounded-md flex items-center justify-center ${
                            showImages 
                              ? 'w-7 h-7' 
                              : 'w-8 h-8'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(item);
                          }}
                          title="Add to cart"
                        >
                          <FiPlus className={showImages ? 'h-4 w-4' : 'h-4 w-4'} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Cart & Placed Orders - Fixed width */}
        <div className="w-full lg:w-80 xl:w-96 bg-white rounded-lg border-0 shadow-sm flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-4 border-b border-gray-200 bg-white">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FiShoppingCart className="h-4 w-4" /> 
              {selectedTable ? `${tables.find(t => t.id === selectedTable)?.table_number || ''}` : 'Cart'}
            </h2>
            
          </div>

          <div className="flex-1 overflow-y-auto">
            {!selectedTable ? (
              <div className="flex items-center justify-center h-full p-8 text-gray-500">
                <div className="text-center">
                  <p>Select a table to view cart and orders</p>
                </div>
              </div>
            ) : (
              <>
                {/* Top Section - Selected Items (Cart) */}
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm">Selected Items</h3>
                  
                  {cart.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-xs">
                      <p>No items selected</p>
                      <p className="mt-1">Click items from menu to add</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 mb-4">
                        {cart.map((item, index) => (
                          <div key={`${item.id}-${index}`} className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 text-sm">{item.name}</h4>
                                <p className="text-xs text-gray-600">₹{getCartItemUnitPrice(item).toFixed(2)} each</p>
                              </div>
                              <button
                                onClick={() => removeFromCart(index)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Remove"
                              >
                                <FiTrash2 className="h-3 w-3" />
                              </button>
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                              <button
                                onClick={() => updateCartQuantity(index, -1)}
                                className="bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300 transition-colors"
                              >
                                <FiMinus className="h-3 w-3" />
                              </button>
                              <span className="font-semibold text-gray-900 w-8 text-center text-sm">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateCartQuantity(index, 1)}
                                className="bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300 transition-colors"
                              >
                                <FiPlus className="h-3 w-3" />
                              </button>
                              <span className="ml-auto font-bold text-gray-900 text-sm">
                                ₹{(getCartItemUnitPrice(item) * item.quantity).toFixed(2)}
                              </span>
                            </div>

                            {item.is_apc && (
                              <div className="mb-2">
                                <label className="block text-[11px] text-gray-600 mb-1">APC Price</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.apcUnitPrice ?? ''}
                                  onChange={(e) => updateApcUnitPrice(index, e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-gray-900 focus:border-transparent"
                                  placeholder="Enter price"
                                />
                              </div>
                            )}

                            <input
                              type="text"
                              placeholder="Special instructions..."
                              value={item.specialInstructions || ''}
                              onChange={(e) => updateSpecialInstructions(index, e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-gray-900 focus:border-transparent"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="bg-gray-100 rounded-lg p-3 mb-4">
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Subtotal:</span>
                            <span className="text-gray-900">₹{cartTotal.subtotal.toFixed(2)}</span>
                          </div>
                          {includeGstInBill && (
                            <>
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">CGST (2.5%):</span>
                                <span className="text-gray-900">₹{cartTotal.cgst.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">SGST (2.5%):</span>
                                <span className="text-gray-900">₹{cartTotal.sgst.toFixed(2)}</span>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-300">
                            <span className="text-gray-900">Total:</span>
                            <span className="text-gray-900">₹{cartTotal.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={confirmAndPlaceOrder}
                        disabled={placingOrder}
                        className="w-full bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 font-medium flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <FiCheck className="h-4 w-4" /> {placingOrder ? 'Placing...' : 'Confirm & Place Order'}
                      </button>
                    </>
                  )}
                </div>

                {/* Bottom Section - Placed Orders */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm">Placed Orders</h3>
                  
                  {Object.keys(groupedPlacedOrders).length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-xs">
                      <p>No orders placed yet</p>
                      <p className="mt-1">Confirm items above to place order</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Object.values(groupedPlacedOrders).map((orderGroup) => (
                        <div key={orderGroup.orderNumber} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-sm">
                                Order #{orderGroup.orderNumber}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                                <FiClock className="h-3 w-3" />
                                <span>{formatIST(orderGroup.orderCreatedAt)}</span>
                                <span className="ml-2">
                                  ({getMinutesSinceOrder(orderGroup.orderCreatedAt)} min ago)
                                </span>
                              </div>
                            </div>
                            {/* Show order status based on item statuses */}
                            <div className="flex flex-col gap-1">
                              {orderGroup.items.every(item => (item.itemStatus || item.status) === 'ready') && (
                                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
                                  All Ready
                                </span>
                              )}
                              {orderGroup.items.some(item => (item.itemStatus || item.status) === 'ready') && 
                               !orderGroup.items.every(item => (item.itemStatus || item.status) === 'ready') && (
                                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">
                                  Some Ready
                                </span>
                              )}
                              {!orderGroup.items.some(item => (item.itemStatus || item.status) === 'ready') && (
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                  orderGroup.orderStatus === 'preparing' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {orderGroup.orderStatus}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="space-y-2 mb-3">
                            {clubOrderItems(orderGroup.items).map((clubbedItem) => (
                              <div key={clubbedItem.key} className="bg-white rounded p-2 border border-gray-200">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <p className="font-medium text-gray-900 text-xs">
                                        {clubbedItem.name}
                                      </p>
                                      <span className="text-xs font-semibold text-gray-700">
                                        x{clubbedItem.totalQty}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-600">
                                      ₹{clubbedItem.unitPrice.toFixed(2)} × {clubbedItem.totalQty} = ₹{clubbedItem.totalSubtotal.toFixed(2)}
                                    </p>
                                    {clubbedItem.specialInstructions && (
                                      <p className="text-xs text-gray-500 italic mt-1">
                                        Note: {clubbedItem.specialInstructions}
                                      </p>
                                    )}
                                    <div className="mt-1 flex flex-wrap gap-1.5">
                                      {clubbedItem.entries.map((entry, entryIdx) => (
                                        <span key={entryIdx} className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                          {entry.qty} {String(entry.status || 'pending').toLowerCase()} • {formatIST(entry.itemTime)}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="pt-3 border-t border-gray-200">
                            <div className="bg-gray-100 rounded-lg p-2.5 mb-3">
                              {(() => {
                                const fallbackSubtotal = orderGroup.items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
                                const subtotal = orderGroup.orderSubtotal > 0 ? orderGroup.orderSubtotal : fallbackSubtotal;
                                const fallbackTax = subtotal * 0.05;
                                const storedTax = Number(orderGroup.orderTax || 0);
                                const tax = includeGstInBill
                                  ? (storedTax > 0 ? storedTax : fallbackTax)
                                  : 0;
                                const storedTotal = Number(orderGroup.orderTotal || 0);
                                const total = includeGstInBill
                                  ? (storedTotal > 0 ? storedTotal : (subtotal + tax))
                                  : subtotal;
                                return (
                                  <>
                                    <div className="flex justify-between text-xs mb-1">
                                      <span className="text-gray-600">Subtotal:</span>
                                      <span className="text-gray-900 font-semibold">₹{subtotal.toFixed(2)}</span>
                                    </div>
                                    {includeGstInBill && (
                                      <>
                                        <div className="flex justify-between text-xs mb-1">
                                          <span className="text-gray-600">CGST (2.5%):</span>
                                          <span className="text-gray-900 font-semibold">₹{(tax / 2).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-xs mb-1">
                                          <span className="text-gray-600">SGST (2.5%):</span>
                                          <span className="text-gray-900 font-semibold">₹{(tax / 2).toFixed(2)}</span>
                                        </div>
                                      </>
                                    )}
                                    <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-300">
                                      <span className="text-gray-900">Total:</span>
                                      <span className="text-gray-900">₹{total.toFixed(2)}</span>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                onClick={() => openPreBillEditModal(orderGroup)}
                                disabled={generatingBill || savingPreBillEdit}
                                className={`px-2 py-2 rounded-lg font-medium flex items-center justify-center gap-1.5 text-xs sm:text-sm whitespace-nowrap transition-colors ${
                                  generatingBill || savingPreBillEdit
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                <FiEdit2 className="h-3 w-3" /> Edit
                              </button>
                              <button
                                onClick={openTableTransferModal}
                                disabled={generatingBill || savingPreBillEdit || transferringTable}
                                className={`px-2 py-2 rounded-lg font-medium flex items-center justify-center gap-1.5 text-xs sm:text-sm whitespace-nowrap transition-colors ${
                                  generatingBill || savingPreBillEdit || transferringTable
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                Transfer
                              </button>
                              <button
                                onClick={() => handleGenerateBillClick(
                                  orderGroup.orderId,
                                  orderGroup.orderNumber,
                                  orderGroup.orderSubtotal > 0
                                    ? orderGroup.orderSubtotal
                                    : orderGroup.items.reduce((sum, item) => sum + (item.subtotal || 0), 0)
                                )}
                                disabled={generatingBill || savingPreBillEdit}
                                className={`px-2 py-2 rounded-lg font-medium flex items-center justify-center gap-1.5 text-xs sm:text-sm whitespace-nowrap transition-colors ${
                                  generatingBill || savingPreBillEdit
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-gray-900 text-white hover:bg-gray-800'
                                }`}
                              >
                                <FiPrinter className="h-3 w-3" /> {generatingBill ? 'Generating...' : 'Generate'}
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 text-center mt-2">
                              This will create a bill, mark order as paid, and free the table
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );

  // Bill Generation Modal
  const billModal = (() => {
    if (!showBillModal || !selectedOrderForBill) return null;

    const orderSubtotal = parseFloat(selectedOrderForBill.subtotal || 0);
    const parsedDiscountPercent = Math.min(100, Math.max(0, parseFloat(discountPercent || 0) || 0));
    const parsedDiscountFlat = Math.min(orderSubtotal, Math.max(0, parseFloat(discountFlatAmount || 0) || 0));
    const calculatedDiscountAmount = applyDiscount
      ? discountType === 'amount'
        ? parseFloat(parsedDiscountFlat.toFixed(2))
        : parseFloat(((orderSubtotal * parsedDiscountPercent) / 100).toFixed(2))
      : 0;

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => {
              setShowBillModal(false);
              setSelectedOrderForBill(null);
            }}
          />
          
          {/* Modal */}
          <div
            className="relative bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200 animate-in fade-in zoom-in duration-200"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-black">Generate Bill</h3>
                <p className="text-sm text-gray-600 mt-1">Order #{selectedOrderForBill.orderNumber}</p>
              </div>
              <button
                onClick={() => {
                  setShowBillModal(false);
                  setSelectedOrderForBill(null);
                }}
                className="text-gray-600 hover:text-black transition-colors p-1 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Payment Method Selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mode of Payment
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {paymentMethods.map((method) => (
                    <button
                      key={method}
                      onClick={() => setPaymentMethod(method)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        paymentMethod === method
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {paymentMethodLabel(method)}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Service Charge Toggle */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeServiceCharge === true || includeServiceCharge === 'true' || includeServiceCharge === 1}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      console.log('Service charge checkbox changed:', newValue);
                      setIncludeServiceCharge(newValue);
                    }}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Include Service Charge
                  </span>
                </label>
              </div>

              {/* Discount Toggle + Percent / Flat Amount */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyDiscount}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setApplyDiscount(enabled);
                      if (!enabled) {
                        setDiscountPercent('');
                        setDiscountFlatAmount('');
                      }
                    }}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                  <span className="text-sm font-medium text-gray-700">Apply Discount</span>
                </label>

                {applyDiscount && (
                  <div className="space-y-2">
                    {/* Type toggle */}
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
                      <button
                        type="button"
                        onClick={() => { setDiscountType('percent'); setDiscountFlatAmount(''); }}
                        className={`flex-1 py-1.5 transition-colors ${
                          discountType === 'percent' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        % Percentage
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDiscountType('amount'); setDiscountPercent(''); }}
                        className={`flex-1 py-1.5 transition-colors ${
                          discountType === 'amount' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        ₹ Flat Amount
                      </button>
                    </div>

                    {/* Input + preview */}
                    <div className="grid grid-cols-2 gap-2 items-center">
                      {discountType === 'percent' ? (
                        <input
                          type="text"
                          ref={discountInputRef}
                          inputMode="decimal"
                          pattern="[0-9]*[.]?[0-9]*"
                          autoComplete="off"
                          value={discountPercent}
                          onChange={(e) => handleDiscountPercentChange(e.target.value)}
                          onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                          onBlur={() => {
                            const parsed = parseFloat(discountPercent || 0);
                            if (!Number.isFinite(parsed)) { setDiscountPercent(''); return; }
                            setDiscountPercent(String(Math.min(100, Math.max(0, parsed))));
                          }}
                          onTouchStart={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          placeholder="e.g. 10"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none text-sm"
                        />
                      ) : (
                        <input
                          type="text"
                          ref={discountInputRef}
                          inputMode="decimal"
                          pattern="[0-9]*[.]?[0-9]*"
                          autoComplete="off"
                          value={discountFlatAmount}
                          onChange={(e) => {
                            let v = String(e.target.value || '').replace(/[^0-9.]/g, '');
                            const dot = v.indexOf('.');
                            if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
                            setDiscountFlatAmount(v);
                          }}
                          onBlur={() => {
                            const parsed = parseFloat(discountFlatAmount || 0);
                            if (!Number.isFinite(parsed)) { setDiscountFlatAmount(''); return; }
                            setDiscountFlatAmount(String(Math.min(orderSubtotal, Math.max(0, parsed))));
                          }}
                          onTouchStart={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          placeholder="e.g. 50"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none text-sm"
                        />
                      )}
                      <div className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        Discount: <span className="font-semibold">₹{calculatedDiscountAmount.toFixed(2)}</span>
                        {discountType === 'percent' && orderSubtotal > 0 && parsedDiscountPercent > 0 && (
                          <span className="text-gray-400 ml-1">({parsedDiscountPercent}%)</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Cashier Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cashier Name <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  ref={cashierInputRef}
                  value={cashierName}
                  onChange={(e) => setCashierName(e.target.value)}
                  onTouchStart={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  placeholder="Enter cashier name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none text-sm"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowBillModal(false);
                  setSelectedOrderForBill(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-black hover:bg-gray-900 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => generateBill(selectedOrderForBill.orderId, selectedOrderForBill.orderNumber, calculatedDiscountAmount)}
                disabled={generatingBill}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  generatingBill
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-black hover:bg-gray-900'
                }`}
              >
                {generatingBill ? 'Generating...' : 'Generate Bill'}
              </button>
            </div>
          </div>
        </div>
      );
  })();

  const preBillEditModal = (() => {
    if (!showPreBillEditModal || !selectedOrderForEdit) return null;

    const lineSubtotal = (line) => {
      const menu = menuItems.find((item) => item.id === line.menuItemId);
      const unitPrice = parseFloat(menu?.price || 0);
      const quantity = parseInt(line.quantity, 10) || 0;
      return unitPrice * quantity;
    };

    const subtotal = editOrderItems.reduce((sum, line) => sum + lineSubtotal(line), 0);
    const tax = includeGstInBill ? (subtotal * 0.05) : 0;
    const total = subtotal + tax;

    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={closePreBillEditModal}
        />

        <div
          className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full border border-gray-200 animate-in fade-in zoom-in duration-200 max-h-[88vh] overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-black">Edit Items Before Bill</h3>
              <p className="text-sm text-gray-600 mt-1">Order #{selectedOrderForEdit.orderNumber}</p>
            </div>
            <button
              onClick={closePreBillEditModal}
              className="text-gray-600 hover:text-black transition-colors p-1 rounded-lg hover:bg-gray-100"
              aria-label="Close"
              disabled={savingPreBillEdit}
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh]">
            {editOrderItems.length === 0 ? (
              <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                No items in draft. Add an item to continue.
              </div>
            ) : (
              editOrderItems.map((line) => {
                const menu = menuItems.find((item) => item.id === line.menuItemId);
                const unitPrice = parseFloat(menu?.price || 0);
                const rowSubtotal = lineSubtotal(line);

                return (
                  <div key={line.lineId} className="border border-gray-200 rounded-lg p-3 bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                      <div className="md:col-span-5">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Menu Item</label>
                        <select
                          value={line.menuItemId || ''}
                          onChange={(e) => updatePreBillEditLine(line.lineId, { menuItemId: e.target.value })}
                          className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                          disabled={savingPreBillEdit}
                        >
                          <option value="" disabled>Select menu item</option>
                          {menuItems.map((item) => (
                            <option key={item.id} value={item.id}>{item.name} (₹{item.price})</option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Qty</label>
                        <input
                          type="number"
                          min="0"
                          value={line.quantity}
                          onChange={(e) => updatePreBillEditLine(line.lineId, { quantity: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                          className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                          disabled={savingPreBillEdit}
                        />
                      </div>

                      <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input
                          type="text"
                          value={line.specialInstructions || ''}
                          onChange={(e) => updatePreBillEditLine(line.lineId, { specialInstructions: e.target.value })}
                          placeholder="Optional"
                          className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                          disabled={savingPreBillEdit}
                        />
                      </div>

                      <div className="md:col-span-2 flex md:justify-end justify-between items-center gap-2">
                        <div className="text-xs text-gray-600 md:text-right">
                          <div>₹{unitPrice.toFixed(2)} each</div>
                          <div className="font-semibold text-gray-900">₹{rowSubtotal.toFixed(2)}</div>
                        </div>
                        <button
                          onClick={() => removePreBillEditLine(line.lineId)}
                          className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          title="Remove line"
                          disabled={savingPreBillEdit}
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            <button
              onClick={addPreBillEditLine}
              className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={savingPreBillEdit || menuItems.length === 0}
            >
              + Add Item
            </button>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-900">₹{subtotal.toFixed(2)}</span>
              </div>
              {includeGstInBill && (
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Tax (5%)</span>
                  <span className="font-medium text-gray-900">₹{tax.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-300">
                <span className="font-semibold text-gray-900">Estimated Total</span>
                <span className="font-semibold text-gray-900">₹{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
            <button
              onClick={closePreBillEditModal}
              className="px-4 py-2 text-sm font-medium text-white bg-black hover:bg-gray-900 rounded-lg transition-colors"
              disabled={savingPreBillEdit}
            >
              Cancel
            </button>
            <button
              onClick={savePreBillEdit}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                savingPreBillEdit
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-black hover:bg-gray-900'
              }`}
              disabled={savingPreBillEdit}
            >
              {savingPreBillEdit ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  })();

  const tableTransferModal = (() => {
    if (!showTableTransferModal) return null;

    const sourceTableNumber = tables.find((table) => table.id === selectedTable)?.table_number || '';

    return (
      <div className="fixed inset-0 z-[85] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={closeTableTransferModal}
        />

        <div
          className="relative bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200 animate-in fade-in zoom-in duration-200"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-black">Table Transfer</h3>
              <p className="text-sm text-gray-600 mt-1">Move all orders from {sourceTableNumber}</p>
            </div>
            <button
              onClick={closeTableTransferModal}
              className="text-gray-600 hover:text-black transition-colors p-1 rounded-lg hover:bg-gray-100"
              aria-label="Close"
              disabled={transferringTable}
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 space-y-3 max-h-[52vh] overflow-y-auto">
            {availableTransferTables.length === 0 ? (
              <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                No currently available/free tables found.
              </div>
            ) : (
              <div className="space-y-2">
                {availableTransferTables.map((table) => (
                  <label
                    key={table.id}
                    className={`flex items-center justify-between gap-3 border rounded-lg px-3 py-2 cursor-pointer transition-colors ${
                      transferTargetTableId === table.id
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{table.table_number}</p>
                      <p className="text-xs text-gray-500">Status: {table.status}</p>
                    </div>
                    <input
                      type="radio"
                      name="transfer-target-table"
                      value={table.id}
                      checked={transferTargetTableId === table.id}
                      onChange={(e) => setTransferTargetTableId(e.target.value)}
                      disabled={transferringTable}
                      className="w-4 h-4"
                    />
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
            <button
              onClick={closeTableTransferModal}
              className="px-4 py-2 text-sm font-medium text-white bg-black hover:bg-gray-900 rounded-lg transition-colors"
              disabled={transferringTable}
            >
              Cancel
            </button>
            <button
              onClick={handleTableTransfer}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                transferringTable || !transferTargetTableId
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-black hover:bg-gray-900'
              }`}
              disabled={transferringTable || !transferTargetTableId}
            >
              {transferringTable ? 'Transferring...' : 'Transfer Orders'}
            </button>
          </div>
        </div>
      </div>
    );
  })();

  // Loading state - check before rendering POSContent
  if (loading) {
    const LoadingContent = () => (
      <div className={`${isFullscreen ? 'fixed inset-0 w-screen h-screen bg-gray-50 z-50' : 'min-h-screen bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-gray-900 mx-auto mb-4"></div>
          <div className="text-gray-900 text-xl font-semibold">Loading POS...</div>
        </div>
      </div>
    );

    if (isFullscreen) {
      return <LoadingContent />;
    }
    return (
      <Layout>
        <LoadingContent />
      </Layout>
    );
  }

  // In fullscreen mode, render only POS content without Layout
  if (isFullscreen) {
    return (
      <>
        {posContent}
        {billModal}
        {preBillEditModal}
        {tableTransferModal}
      </>
    );
  }

  // Normal mode with Layout
  return (
    <Layout>
      {posContent}
      {billModal}
      {preBillEditModal}
      {tableTransferModal}
    </Layout>
  );
}

export default POS;
